/**
 * Signup.tsx — mango-web
 * PURPOSE: Sign-up screen mirroring Android's SignUpScreen.kt.
 * Split-screen layout matching Login.tsx.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// --- Helpers ---

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// --- Component ---

/**
 * PURPOSE: Render the split-screen sign-up form.
 * - Connected to: stores/authStore.ts → signUpWithEmail
 * - Used by: App.tsx → route "/signup"
 */
export function Signup() {
  const navigate = useNavigate();
  const { user, isLoading, error, signUpWithEmail, clearError } = useAuthStore();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // --- Validation ---
  const isValidEmail = EMAIL_REGEX.test(email);
  const emailError = emailTouched && email.length > 0 && !isValidEmail
    ? 'please enter a valid email.' : '';
  const passwordError = password.length > 0 && password.length < 6
    ? 'password must be at least 6 characters.' : '';
  const confirmError = confirmPassword.length > 0 && confirmPassword !== password
    ? 'passwords do not match.' : '';
  const canSubmit = fullName.trim().length > 0 && isValidEmail && password.length >= 6 && password === confirmPassword && !isLoading;

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const ok = await signUpWithEmail(email, password, fullName);
    if (ok) setSignUpSuccess(true);
  };

  // --- Success state ---
  if (signUpSuccess) {
    return (
      <div className="auth-split">
        <div className="auth-form-side">
          <div className="auth-form-inner auth-magic-sent">
            <div className="magic-sent-icon">🎉</div>
            <h2 className="auth-title font-display">check your email</h2>
            <p className="auth-subtitle">
              we sent a verification link to <strong>{email}</strong>
            </p>
            <Link to="/login" className="ghost-link">back to sign in</Link>
          </div>
        </div>
        <div className="auth-illust-side">
          <img src="/auth-signup.svg" alt="" className="auth-illust-img" />
          <div className="auth-illust-overlay" />
        </div>
      </div>
    );
  }

  // --- Main form ---
  return (
    <div className="auth-split">
      {/* Left — Form */}
      <div className="auth-form-side">
        <div className="auth-form-inner">

          <div className="auth-brand">
            <img src="/mango-logo.png" alt="mango" className="auth-logo-img" />
            <h1 className="auth-brand-name font-display">mango</h1>
            <p className="auth-tagline font-mono">TWILIO CALLS · MESSAGES</p>
          </div>

          <h2 className="auth-title font-display">create account</h2>

          <form onSubmit={handleSignUp} className="auth-form">
            <div className="input-group">
              <input
                type="text"
                className="auth-input"
                placeholder="full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                autoFocus
              />
            </div>

            <div className="input-group">
              <input
                type="email"
                className={`auth-input ${emailError ? 'auth-input-error' : ''}`}
                placeholder="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (!emailTouched && e.target.value.length > 0) setEmailTouched(true);
                }}
                autoComplete="email"
              />
              {emailError && <span className="input-error-text">{emailError}</span>}
            </div>

            <div className="input-group">
              <div className="input-password-wrap">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  className={`auth-input ${passwordError ? 'auth-input-error' : ''}`}
                  placeholder="password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  aria-label={passwordVisible ? 'hide password' : 'show password'}
                >
                  {passwordVisible ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && <span className="input-error-text">{passwordError}</span>}
            </div>

            <div className="input-group">
              <div className="input-password-wrap">
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  className={`auth-input ${confirmError ? 'auth-input-error' : ''}`}
                  placeholder="confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              {confirmError && <span className="input-error-text">{confirmError}</span>}
            </div>

            {error && <div className="auth-error">{error}</div>}

            <button type="submit" className="volt-btn" disabled={!canSubmit}>
              <span className="btn-label">
                {isLoading ? 'creating account...' : 'sign up'}
              </span>
            </button>
          </form>

          <div className="auth-footer-links">
            <p className="auth-footer-row">
              <span className="auth-footer-muted">already have an account?</span>
              <Link to="/login" className="auth-footer-link">sign in</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right — Illustration */}
      <div className="auth-illust-side">
        <img src="/auth-signup.svg" alt="" className="auth-illust-img" />
        <div className="auth-illust-overlay" />
      </div>
    </div>
  );
}

// --- Related Files ---
// CONNECTED TO:
//   - src/stores/authStore.ts — signUpWithEmail
//   - src/styles/auth.css — split-screen layout styles
// USED BY:
//   - src/App.tsx — route "/signup"
